import { LockOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import { useAuth } from '../auth/useAuth';

/** Giá trị của biểu mẫu đổi mật khẩu. */
interface ChangePasswordFormValues {
  readonly currentPassword: string;
  readonly newPassword: string;
  readonly confirmPassword: string;
}

/**
 * Trang đổi mật khẩu của chính người đang đăng nhập.
 *
 * Ba điểm đáng nói về cách trang này hoạt động:
 *
 * 1. **Vẫn hỏi mật khẩu hiện tại dù người dùng đã đăng nhập.** Phiếu truy cập sống mười lăm phút,
 *    nên một người ngồi vào máy đang mở của người khác vẫn còn phiên hợp lệ. Không hỏi mật khẩu
 *    hiện tại thì họ đổi được mật khẩu và chiếm hẳn tài khoản.
 * 2. **Ô nhập lại mật khẩu chỉ do giao diện kiểm tra**, không gửi lên máy chủ. Nó chống lỗi đánh máy,
 *    không phải một quy tắc nghiệp vụ — gửi lên để máy chủ kiểm tra lại là gửi thừa một bản mật khẩu.
 * 3. **Quy tắc mật khẩu do máy chủ quyết định.** Giao diện không nhân bản danh sách quy tắc đó:
 *    hai bản sẽ lệch nhau, và khi lệch thì người dùng thấy ô nhập báo hợp lệ rồi bị máy chủ từ chối.
 *    Giao diện chỉ chặn ô trống và hiển thị nguyên văn lý do máy chủ trả về.
 */
export function ChangePasswordPage(): ReactNode {
  const { user, changePassword } = useAuth();

  const [form] = Form.useForm<ChangePasswordFormValues>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(values: ChangePasswordFormValues): Promise<void> {
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await changePassword(values.currentPassword, values.newPassword);

      setIsSuccessful(true);
      form.resetFields();
    } catch (error) {
      setErrorMessage(
        error instanceof ApiError
          ? error.message
          : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const mustChange = user?.mustChangePassword === true;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Đổi mật khẩu
      </Typography.Title>

      {mustChange && (
        <Alert
          type="warning"
          showIcon
          message="Tài khoản này đang dùng mật khẩu tạm"
          description={
            'Mật khẩu tạm được đặt lúc cài đặt hệ thống nên có thể đã đi qua nhiều tay. '
            + 'Hãy đổi sang mật khẩu chỉ mình bạn biết trước khi làm việc khác.'
          }
        />
      )}

      {isSuccessful && (
        <Alert
          type="success"
          showIcon
          message="Đã đổi mật khẩu"
          description={
            'Các thiết bị khác đang đăng nhập bằng tài khoản này đã bị đăng xuất. '
            + 'Thiết bị bạn đang dùng vẫn giữ nguyên phiên làm việc.'
          }
        />
      )}

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Card>
        <Form<ChangePasswordFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => {
            void handleSubmit(values);
          }}
          requiredMark={false}
          disabled={isSubmitting}
        >
          <Form.Item
            name="currentPassword"
            label="Mật khẩu hiện tại"
            rules={[{ required: true, message: 'Chưa nhập mật khẩu hiện tại.' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              autoComplete="current-password"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="Mật khẩu mới"
            rules={[{ required: true, message: 'Chưa nhập mật khẩu mới.' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Nhập lại mật khẩu mới"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Chưa nhập lại mật khẩu mới.' },

              /*
                So hai ô ngay tại giao diện. Đây là phép chống lỗi đánh máy, không phải quy tắc
                nghiệp vụ — nên nó không được gửi lên máy chủ.
              */
              ({ getFieldValue }) => ({
                validator: (_rule, value: unknown) =>
                  typeof value !== 'string' || value === getFieldValue('newPassword')
                    ? Promise.resolve()
                    : Promise.reject(new Error('Hai lần nhập mật khẩu mới không giống nhau.')),
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Đổi mật khẩu
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Đổi mật khẩu sẽ đăng xuất tài khoản này khỏi mọi thiết bị khác. Đó là chủ ý: nếu bạn đổi mật
        khẩu vì nghi ngờ bị lộ, thì việc chấm dứt các phiên khác mới là phần có tác dụng.
      </Typography.Paragraph>
    </div>
  );
}
